import {
  ArrowLeft,
  CircleDollarSign,
  FolderKanban,
  LayoutDashboard,
  Network,
  SlidersHorizontal,
  UsersRound,
} from 'lucide-react';

import type { NavItem } from '@/lib/adminNav';

const ORG_SCOPE_PATH = /^\/organizations\/([^/]+)(?:\/|$)/;

/** The org id when the path is inside one organization, so the sidebar swaps to its sections. */
export function orgIdFromPath(pathname: string): string | undefined {
  const match = ORG_SCOPE_PATH.exec(pathname);
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return undefined;
  }
}

export function orgBasePath(orgId: string): string {
  return `/organizations/${encodeURIComponent(orgId)}`;
}

export type OrgSection = 'projects' | 'members' | 'fabric' | 'integration-settings' | 'money';

export function orgSectionPath(orgId: string, section?: OrgSection): string {
  return section ? `${orgBasePath(orgId)}/${section}` : orgBasePath(orgId);
}

/** The parts of the Money page, in page order; each is also its anchor. */
export const MONEY_SECTIONS = ['spend', 'plan', 'credits'] as const;
export type MoneySection = (typeof MONEY_SECTIONS)[number];

export function orgMoneyPath(orgId: string, section?: MoneySection): string {
  const path = orgSectionPath(orgId, 'money');
  return section ? `${path}#${section}` : path;
}

/** Side panels the Money page can open straight from `?panel=`. */
export const MONEY_PANELS = ['plan', 'grant-credit', 'history', 'promo', 'invoices'] as const;
export type MoneyPanel = (typeof MONEY_PANELS)[number];

export function parseMoneyPanel(value: string | null): MoneyPanel | undefined {
  return MONEY_PANELS.find((panel) => panel === value);
}

/** Usage, Billing and Credits were separate pages before Money; their old paths land on the matching part. */
export const LEGACY_MONEY_PATHS: Record<string, MoneySection> = {
  usage: 'spend',
  billing: 'plan',
  credits: 'credits',
};

/** Old `?tab=` links from before the org sections moved into the sidebar. */
export function legacyOrgTabPath(orgId: string, searchParams: URLSearchParams): string | undefined {
  const tab = searchParams.get('tab');
  if (tab === 'projects') return orgSectionPath(orgId, 'projects');
  if (tab === 'realms') return orgSectionPath(orgId, 'fabric');
  if (tab === 'access') return orgSectionPath(orgId, 'members');
  if (tab === 'usage') return orgMoneyPath(orgId, 'spend');
  if (tab === 'billing') return orgMoneyPath(orgId, searchParams.get('billing') === 'credits' ? 'credits' : 'plan');
  return undefined;
}

export function orgNavItems(orgId: string): NavItem[] {
  return [
    { path: '/organizations', label: 'Organizations', icon: ArrowLeft, group: 'back' },
    { path: orgSectionPath(orgId), label: 'Overview', icon: LayoutDashboard, group: 'org' },
    { path: orgSectionPath(orgId, 'projects'), label: 'Projects', icon: FolderKanban, group: 'org' },
    { path: orgSectionPath(orgId, 'members'), label: 'People', icon: UsersRound, group: 'org' },
    { path: orgSectionPath(orgId, 'fabric'), label: 'Fabric', icon: Network, group: 'org' },
    { path: orgSectionPath(orgId, 'integration-settings'), label: 'Integration settings', icon: SlidersHorizontal, group: 'org' },
    { path: orgSectionPath(orgId, 'money'), label: 'Money', icon: CircleDollarSign, group: 'org' },
  ];
}

/**
 * Sessions and runs live under a project, so they keep Projects lit. Overview
 * and the back link light only on their exact path.
 */
export function isOrgNavItemActive(currentPath: string, orgId: string, itemPath: string): boolean {
  const base = orgBasePath(orgId);
  if (itemPath === '/organizations' || itemPath === base) return currentPath === itemPath;
  if (itemPath === `${base}/projects`) {
    return [`${base}/projects`, `${base}/sessions`, `${base}/runs`].some(
      (prefix) => currentPath === prefix || currentPath.startsWith(`${prefix}/`),
    );
  }
  return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);
}
